import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper
} from '@mui/material';
import {
  Info as InfoIcon,
  Storage as DatabaseIcon,
  Cloud as ServerIcon,
  Business as TenantIcon,
  People as UsersIcon,
  Folder as StorageIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  Schedule as UptimeIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const SystemInfoPage = () => {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSystemInfo();
  }, []);

  const fetchSystemInfo = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/system-info');
      setInfo(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch system information');
    } finally {
      setLoading(false);
    }
  };

  const copyTenantId = () => {
    if (info?.tenant?.tenantId) {
      navigator.clipboard.writeText(info.tenant.tenantId);
      setCopied(true);
      toast.success('Tenant ID copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ') || '< 1m';
  };

  const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || '-'}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!info) {
    return (
      <Alert severity="error">Failed to load system information</Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            System Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View tenant configuration and system details
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={fetchSystemInfo}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Tenant Information - Full Width */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
              color: 'white',
              borderRadius: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TenantIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>Tenant Information</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {info.tenant.isTemplate ? 'Template / Development Instance' : 'Provisioned Instance'}
                </Typography>
              </Box>
              {info.tenant.isTemplate ? (
                <Chip label="TEMPLATE" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              ) : (
                <Chip label="PROVISIONED" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} icon={<CheckIcon sx={{ color: 'white !important' }} />} />
              )}
            </Box>

            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              p: 3, 
              borderRadius: 2, 
              textAlign: 'center',
              mb: 3,
              backdropFilter: 'blur(10px)'
            }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>TENANT ID</Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontWeight: 700,
                  letterSpacing: 4,
                  my: 1
                }}
              >
                {info.tenant.tenantId}
              </Typography>
              <Button 
                variant="contained"
                size="small" 
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={copyTenantId}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                {copied ? 'Copied!' : 'Copy Tenant ID'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 2 }}>
                  <Typography variant="overline" sx={{ opacity: 0.7 }}>Tenant Name</Typography>
                  <Typography variant="h6">{info.tenant.tenantName || '-'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 2 }}>
                  <Typography variant="overline" sx={{ opacity: 0.7 }}>Plan</Typography>
                  <Typography variant="h6">{(info.tenant.plan || 'Standard').toUpperCase()}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: 2 }}>
                  <Typography variant="overline" sx={{ opacity: 0.7 }}>Max Users</Typography>
                  <Typography variant="h6">{info.tenant.maxUsers || 50}</Typography>
                </Box>
              </Grid>
            </Grid>

            {info.tenant.isTemplate && (
              <Alert severity="info" sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.9)' }}>
                <strong>Development Mode:</strong> Set <code>TENANT_ID</code> environment variable to provision this instance for production use.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <UsersIcon color="primary" />
                <Typography variant="h6">User Statistics</Typography>
              </Box>
              <InfoRow label="Total Users" value={info.statistics?.totalUsers} />
              <InfoRow label="Active Users" value={info.statistics?.activeUsers} />
              <InfoRow label="Inactive Users" value={info.statistics?.inactiveUsers} />
            </CardContent>
          </Card>
        </Grid>

        {/* Server Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ServerIcon color="primary" />
                <Typography variant="h6">Server</Typography>
              </Box>
              <InfoRow label="Node.js" value={info.server?.nodeVersion} />
              <InfoRow 
                label="Environment" 
                value={
                  <Chip 
                    label={info.server?.environment} 
                    size="small" 
                    color={info.server?.environment === 'production' ? 'success' : 'warning'} 
                  />
                } 
              />
              <InfoRow 
                label="Uptime" 
                value={formatUptime(info.server?.uptime)}
                icon={<UptimeIcon fontSize="small" sx={{ color: '#10b981' }} />}
              />
              <InfoRow label="Memory" value={`${info.server?.memoryUsage?.heapUsed}/${info.server?.memoryUsage?.heapTotal} MB`} />
              <InfoRow label="Platform" value={`${info.server?.platform} (${info.server?.architecture})`} />
            </CardContent>
          </Card>
        </Grid>

        {/* Database Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DatabaseIcon color="primary" />
                <Typography variant="h6">Database</Typography>
              </Box>
              <InfoRow label="Type" value={info.database?.type} />
              <InfoRow label="Host" value={info.database?.host} />
              <InfoRow label="Port" value={info.database?.port} />
              <InfoRow label="Database" value={info.database?.name} />
            </CardContent>
          </Card>
        </Grid>

        {/* Application Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoIcon color="primary" />
                <Typography variant="h6">Application</Typography>
              </Box>
              <InfoRow label="Name" value={info.application?.name} />
              <InfoRow label="Version" value={info.application?.version} />
              <InfoRow label="Frontend URL" value={info.application?.frontendUrl} />
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorageIcon color="primary" />
                <Typography variant="h6">Storage</Typography>
              </Box>
              <InfoRow label="Upload Path" value={info.storage?.uploadPath} />
              <InfoRow label="Documents Path" value={info.storage?.documentsPath} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemInfoPage;
